import { useId, useState } from 'react'
import { Link } from 'react-router-dom'
import { ChevronDown, ExternalLink, ListChecks, Plus, Trash2 } from 'lucide-react'
import Button from '../../components/Button'
import FormRow from '../../components/FormRow'
import IconButton from '../../components/IconButton'
import Select from '../../components/Select'
import StatusBadge from '../../components/StatusBadge'
import TextField from '../../components/TextField'
import { listQuestions } from '../content/contentService'
import { strings as S } from './strings'

/**
 * One rule, and the questions it draws from.
 *
 * The preview lists the topic's questions AT THE RULE'S DIFFICULTY only — the pool one
 * attempt picks `count` from. It used to list the whole topic, so a rule asking for one
 * hard question showed all twenty.
 */
function RuleRow({ rule, onRemove }) {
  const [open, setOpen] = useState(false)
  const [preview, setPreview] = useState(null)
  const panelId = useId()

  const toggle = async () => {
    const next = !open
    setOpen(next)
    if (!next || preview?.rows) return

    setPreview({ loading: true })
    try {
      const result = await listQuestions({ categoryId: rule.topicId, difficultyId: rule.levelId })
      setPreview({ rows: result.rows })
    } catch {
      setPreview({ error: true })
    }
  }

  const rows = preview?.rows
  const short = rows && rows.length < rule.count

  return (
    <li className={`exam-rule${open ? ' is-open' : ''}`}>
      <div className="exam-rule__row">
        <span className="exam-rule__text">
          <bdi className="exam-rule__topic">{rule.topic}</bdi>
          <span className="exam-rule__meta">
            {/* The question bank's own difficulty chip, so a level looks the same in both. */}
            <StatusBadge tone="primary" plain>
              {rule.level}
            </StatusBadge>
            <span>{S.rules.questions(rule.count)}</span>
          </span>
        </span>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="exam-rule__toggle"
          aria-expanded={open}
          aria-controls={panelId}
          onClick={toggle}
        >
          {open ? S.rules.hideQuestions : S.rules.viewQuestions}
          <ChevronDown size={16} strokeWidth={2} aria-hidden="true" />
        </Button>
        <IconButton icon={Trash2} label={S.rules.remove} tone="danger" onClick={onRemove} />
      </div>

      {open && (
        <div className="exam-rule__questions" id={panelId}>
          <div className="exam-rule__questions-head">
            {preview?.loading && <p role="status">{S.rules.loadingQuestions}</p>}
            {preview?.error && <p role="alert">{S.rules.questionsFailed}</p>}
            {rows && rows.length === 0 && <p className="is-warning">{S.rules.noQuestions}</p>}
            {rows && rows.length > 0 && (
              <p className={short ? 'is-warning' : undefined}>
                {short ? S.rules.shortfall(rows.length, rule.count) : S.rules.draws(rule.count, rows.length)}
              </p>
            )}
            <Button
              as={Link}
              to={`/questions?categoryId=${rule.topicId}`}
              target="_blank"
              rel="noopener noreferrer"
              variant="ghost"
              size="sm"
            >
              {S.rules.manageQuestions}
              <ExternalLink size={14} strokeWidth={2} aria-hidden="true" />
            </Button>
          </div>
          {rows?.length > 0 && (
            <ol className="exam-rule__list">
              {rows.map((question) => (
                <li key={question.id}>
                  <bdi>{question.text}</bdi>
                </li>
              ))}
            </ol>
          )}
        </div>
      )}
    </li>
  )
}

/** The exam's question rules: the list, and one line of fields that adds to it. */
export default function ExamRules({
  rules,
  onRemove,
  draft,
  topics,
  levels,
  available,
  error,
  onTopic,
  onLevel,
  onCount,
  onAdd,
}) {
  const titleId = useId()
  const total = rules.reduce((sum, rule) => sum + Number(rule.count || 0), 0)

  return (
    <section className="exam-rules" aria-labelledby={titleId}>
      <header className="exam-rules__head">
        <span className="exam-rules__icon" aria-hidden="true">
          <ListChecks size={18} strokeWidth={1.75} />
        </span>
        <h3 className="exam-rules__title" id={titleId}>
          <bdi>{S.rules.title}</bdi>
        </h3>
        {rules.length > 0 && (
          /* The sum of the rows' counts: how many questions one attempt has. */
          <p className="exam-rules__total">
            <span>{S.rules.total}</span>
            <strong>{total}</strong>
          </p>
        )}
      </header>

      {rules.length === 0 ? (
        <p className="exam-rules__empty">
          <bdi>{S.rules.empty}</bdi>
        </p>
      ) : (
        <ul className="exam-rules__list">
          {rules.map((rule, index) => (
            <RuleRow key={rule.id} rule={rule} onRemove={() => onRemove(index)} />
          ))}
        </ul>
      )}

      <div className="exam-rules__composer">
        <FormRow label={S.rules.topic}>
          {(id) => <Select id={id} value={draft.topicId} onChange={onTopic} options={topics} />}
        </FormRow>

        <FormRow label={S.rules.level}>
          {(id) => (
            <Select id={id} value={draft.levelId} onChange={onLevel} options={levels} disabled={!draft.topicId} />
          )}
        </FormRow>

        <FormRow
          label={S.rules.count}
          error={error}
          /* The cap, from the server — shown under the field so the number is typed with
             it in view, not only refused after. */
          helper={available == null ? undefined : `${S.rules.available}: ${available}`}
        >
          {(id) => (
            <TextField
              id={id}
              dir="ltr"
              inputMode="numeric"
              value={draft.count}
              onChange={onCount}
              disabled={!draft.levelId}
              invalid={!!error}
            />
          )}
        </FormRow>

        <Button type="button" variant="soft" className="exam-rules__add" onClick={onAdd} disabled={!draft.count}>
          <Plus size={16} strokeWidth={2} aria-hidden="true" />
          {S.rules.add}
        </Button>
      </div>
    </section>
  )
}
